import { calculateDrawings, completedUpto } from '../typeOfWork.calculations';
import { GROUND_LEVEL_TYPE, WORK_AGE, WORK_CATEGORY } from '../work.constants';

/**
 * Translates the Type of Work form to and from `job_floors` rows.
 *
 * The row shape is not free-form: `WorkJob`'s delegate getters read the floor set back by
 * `floor_type.code` and, for ground, by `variant` — `null` for the summary row that holds
 * `ground_level_type`, then `single` / `lower` / `upper` for the usage rows — and
 * `getUpperFloorGroups()` looks specifically at the `upper` row whose variant is null.
 * `AnalystController.saveJobFloors`/`upsertGroundFloor` write the same layout from the
 * seven-step wizard, so a job edited here reads back exactly like one created there.
 */

/** One `job_floors` row as `PUT /api/job-floors/job/{id}` takes it (snake_case, like the rest of the API). */
export interface JobFloorRowPayload {
  floor_type_code: string;
  floor_count?: number | null;
  /** `existing` row only (V17): the floor the new work starts at. */
  starting_floor_no?: number | null;
  drawings_count?: number | null;
  variant?: string | null;
  usage_type?: string | null;
  is_mezzanine?: boolean;
  column_footing_required?: boolean;
  ground_level_type?: string | null;
  metadata?: unknown;
}

/** A row as the API returns it — the entity serializes camelCase, with floorType nested. */
export interface JobFloorRow {
  id?: number;
  floorType?: { id?: number; code?: string; label?: string } | null;
  floorCount?: number | null;
  startingFloorNo?: number | null;
  drawingsCount?: number | null;
  variant?: string | null;
  usageType?: string | null;
  isMezzanine?: boolean | null;
  columnFootingRequired?: boolean | null;
  groundLevelType?: string | null;
  metadata?: string | null;
  [key: string]: unknown;
}

const num = (value: unknown): number => Number(value || 0);

const isBuildingCategory = (workCategory: unknown) =>
  workCategory === WORK_CATEGORY.BUILDING || workCategory === WORK_CATEGORY.ALLIED_BUILDING;

/**
 * The floor set described by the current form values.
 *
 * Allied-only work has no building to describe, so it contributes just the two rows that
 * exist for every job — column footing and structural. `calculateDrawings` already returns
 * zeroes for the rest in that case, but writing the rows anyway would claim a ground floor
 * and an upper floor that the job does not have.
 */
export function buildJobFloorRows(values: Record<string, any>): JobFloorRowPayload[] {
  const drawings = calculateDrawings(values);
  const isBuilding = isBuildingCategory(values.workCategory);
  const isNewWork = values.workAge === WORK_AGE.NEW;
  const isExistingWork = values.workAge === WORK_AGE.EXISTING;
  const isSingleGround = values.groundLevelType === GROUND_LEVEL_TYPE.SINGLE;
  const isMultipleGround = values.groundLevelType === GROUND_LEVEL_TYPE.MULTIPLE;

  const rows: JobFloorRowPayload[] = [
    { floor_type_code: 'column', drawings_count: drawings.columnFootingDrawings || 0 },
    {
      floor_type_code: 'structural',
      floor_count: drawings.slabsCount,
      drawings_count: drawings.structuralDrawings,
    },
  ];

  if (isBuilding) {
    rows.push({
      floor_type_code: 'basement',
      floor_count: values.hasBasement ? num(values.basementCount) : 0,
      drawings_count: drawings.basementDrawings,
    });

    // The variant-less ground row is the summary: it carries the single/multiple choice and
    // the drawing count. The usage rows below hang off it.
    rows.push({
      floor_type_code: 'ground',
      variant: null,
      ground_level_type: values.groundLevelType || null,
      drawings_count: drawings.groundLevelDrawings,
    });

    if (isSingleGround) {
      rows.push({
        floor_type_code: 'ground',
        variant: 'single',
        usage_type: values.singleGroundUsage || null,
        is_mezzanine: Boolean(values.singleHasMezzanine),
      });
    }

    if (isMultipleGround) {
      rows.push({
        floor_type_code: 'ground',
        variant: 'lower',
        usage_type: values.lowerGroundUsage || null,
        is_mezzanine: Boolean(values.lowerGroundHasMezzanine),
        column_footing_required: Boolean(values.lowerGroundRequiresColumnFooting),
      });
      rows.push({
        floor_type_code: 'ground',
        variant: 'upper',
        usage_type: values.upperGroundUsage || null,
        is_mezzanine: Boolean(values.upperGroundHasMezzanine),
      });
    }

    rows.push({
      floor_type_code: 'upper',
      variant: null,
      floor_count: num(values.upperFloorsCount),
      drawings_count: drawings.upperFloorDrawings || 0,
      metadata: values.upperFloorGroups ?? null,
    });

    // "Provision for additional floors" is only asked for new work.
    if (isNewWork) {
      rows.push({
        floor_type_code: 'additional',
        floor_count: values.hasAdditionalFloors ? num(values.additionalFloorsCount) : 0,
      });
    }

    // Staircase cap / OHWT / LMR. Answered "no" it is written as zero rather than left out,
    // so re-opening the panel shows the answer that was given instead of the form default.
    rows.push({
      floor_type_code: 'staircase_ohwt',
      floor_count: values.hasStaircaseOhwt === false ? 0 : num(values.staircaseOhwtCount),
      drawings_count: drawings.staircaseOhwtSlabs || 0,
    });
  }

  if (isExistingWork) {
    /**
     * The screen asks how far the work is completed; the new work starts on the floor above.
     * Both are written: `starting_floor_no` is that floor, `floor_count` (the floors already
     * standing) keeps the shape every older reader of this row expects.
     */
    const completed = completedUpto(values);
    rows.push({
      floor_type_code: 'existing',
      floor_count: completed ?? 0,
      starting_floor_no: completed == null ? null : completed + 1,
      drawings_count: drawings.existingFloorDrawings || 0,
    });
  }

  return rows;
}

/** `metadata` comes back as a JSON *string*; anything unparseable is treated as absent. */
function parseGroups(metadata: string | null | undefined) {
  if (!metadata) return null;
  try {
    const parsed = JSON.parse(metadata);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * The saved floor set as Type of Work form values, so reopening the panel shows what is
 * stored rather than a blank form.
 *
 * Only the keys the floors actually determine are returned — the caller merges them over
 * the form defaults, so anything absent keeps its default. A number is returned as a
 * string because the form's numeric fields are text inputs and `null`/`undefined` would
 * make them uncontrolled.
 */
export function jobFloorRowsToFormValues(rows: JobFloorRow[]): Record<string, any> {
  const byCode = (code: string, variant: string | null = null) =>
    rows.find(
      (row) => row.floorType?.code === code && (row.variant ?? null) === variant
    ) ?? null;

  const str = (value: number | null | undefined) =>
    value === null || value === undefined ? '' : String(value);

  const basement = byCode('basement');
  const ground = byCode('ground');
  const single = byCode('ground', 'single');
  const lower = byCode('ground', 'lower');
  const upperGround = byCode('ground', 'upper');
  const upper = byCode('upper');
  const additional = byCode('additional');
  const existing = byCode('existing');
  const structural = byCode('structural');
  const staircase = byCode('staircase_ohwt');

  const values: Record<string, any> = {};

  if (basement) {
    // A stored 0 means "asked and answered: no basement", which is not the same as the
    // untouched `null` the form starts at.
    values.hasBasement = num(basement.floorCount) > 0;
    values.basementCount = num(basement.floorCount) > 0 ? str(basement.floorCount) : '';
  }

  if (ground?.groundLevelType) values.groundLevelType = ground.groundLevelType;

  if (single) {
    values.singleGroundUsage = single.usageType ?? '';
    values.singleHasMezzanine = Boolean(single.isMezzanine);
  }
  if (lower) {
    values.lowerGroundUsage = lower.usageType ?? '';
    values.lowerGroundHasMezzanine = Boolean(lower.isMezzanine);
    values.lowerGroundRequiresColumnFooting = Boolean(lower.columnFootingRequired);
  }
  if (upperGround) {
    values.upperGroundUsage = upperGround.usageType ?? '';
    values.upperGroundHasMezzanine = Boolean(upperGround.isMezzanine);
  }

  if (upper) {
    values.upperFloorsCount = num(upper.floorCount) > 0 ? str(upper.floorCount) : '';
    const groups = parseGroups(upper.metadata);
    if (groups) values.upperFloorGroups = groups;
  }

  if (additional) {
    values.hasAdditionalFloors = num(additional.floorCount) > 0;
    values.additionalFloorsCount = num(additional.floorCount) > 0 ? str(additional.floorCount) : '';
  }

  if (existing) {
    /**
     * Prefer the stored starting floor; fall back to the count of existing floors plus one
     * for jobs saved before V17 added the column, which is exactly what it meant.
     */
    const startingFloorNo =
      num(existing.startingFloorNo) > 0 ? num(existing.startingFloorNo) : num(existing.floorCount) + 1;
    // Shown as "Work Completed Upto": the floor below the one the new work starts at.
    values.workCompletedUpto = str(startingFloorNo - 1);
  }

  if (staircase) {
    // A stored 0 is "asked and answered: none", which the form default (yes, one) is not.
    values.hasStaircaseOhwt = num(staircase.floorCount) > 0;
    values.staircaseOhwtCount = num(staircase.floorCount) > 0 ? str(staircase.floorCount) : '';
  }

  // Slabs are stored on the structural row's count; the drawings figure beside it is
  // derived, so only the editable number is restored.
  if (structural && num(structural.floorCount) > 0) {
    values.slabsCount = str(structural.floorCount);
  }

  return values;
}
